import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { WhiteboardCanvas } from '../components/whiteboard/WhiteboardCanvas';
import { WhiteboardToolbar } from '../components/whiteboard/WhiteboardToolbar';
import { useWebSocket } from '../hooks/useWebSocket';
import { ArrowLeft, Users, Wifi, WifiOff, Eye, Edit3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

const generateId = () => `obj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const SharedWhiteboardPage = () => {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  
  // Canvas state
  const [objects, setObjects] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeTool, setActiveTool] = useState('select');
  const [connectorType, setConnectorType] = useState('elbow');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [gridType, setGridType] = useState('dots');
  const [snapToGrid, setSnapToGrid] = useState(true);
  
  // Whiteboard data
  const [boardId, setBoardId] = useState(null);
  const [boardName, setBoardName] = useState('Shared Whiteboard');
  const [permission, setPermission] = useState('view');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pen settings
  const [penSettings, setPenSettings] = useState({
    color: '#1E293B',
    width: 2,
    mode: 'pen',
    pressureSensitive: true
  });
  
  // Shape settings
  const [shapeSettings, setShapeSettings] = useState({
    fillColor: '#FFFFFF',
    strokeColor: '#1E293B',
    strokeWidth: 2,
    fillOpacity: 1,
    fontSize: 14,
    textColor: '#1E293B'
  });

  // Generate stable guest ID
  const guestId = useRef(`guest_${Date.now()}`);
  
  // WebSocket for real-time collaboration
  const {
    isConnected,
    users,
    remoteCursors,
    setHandlers,
    sendCursorMove,
    sendObjectCreated,
    sendObjectUpdated,
    sendObjectDeleted,
  } = useWebSocket(
    boardId,
    token,
    user || { id: guestId.current, name: 'Guest' },
    !isLoading && boardId && permission === 'edit'
  );

  // Load shared whiteboard
  useEffect(() => {
    const loadSharedWhiteboard = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/whiteboards/shared/${shareToken}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('This shared whiteboard link is invalid or has expired.');
          } else {
            setError('Failed to load whiteboard.');
          }
          setIsLoading(false);
          return;
        }
        
        const data = await response.json();
        setBoardId(data.id);
        setBoardName(data.name);
        setObjects(data.objects || []);
        setPermission(data.permission || 'view');
        setIsLoading(false);
        
      } catch (err) {
        console.error('Error loading shared whiteboard:', err);
        setError('Failed to load whiteboard. Please try again.');
        setIsLoading(false);
      }
    };

    loadSharedWhiteboard();
  }, [shareToken]);

  // Set up WebSocket handlers
  useEffect(() => {
    if (permission !== 'edit') return;
    
    setHandlers({
      onObjectCreated: (object, userId) => {
        if (userId === user?.id) return;
        setObjects(prev => {
          if (prev.find(o => o.id === object.id)) return prev;
          return [...prev, object];
        });
      },
      onObjectUpdated: (objectId, changes, userId) => {
        if (userId === user?.id) return;
        setObjects(prev => prev.map(obj =>
          obj.id === objectId ? { ...obj, ...changes } : obj
        ));
      },
      onObjectDeleted: (objectIds, userId) => {
        if (userId === user?.id) return;
        setObjects(prev => prev.filter(obj => !objectIds.includes(obj.id)));
      },
    });
  }, [setHandlers, user?.id, permission]);

  // Add object (only if has edit permission)
  const addObject = (type, position, options = {}) => {
    if (permission !== 'edit') {
      toast.error('You only have view access to this board');
      return null;
    }

    const newObject = {
      id: generateId(),
      type,
      x: position.x,
      y: position.y,
      width: options.width || 120,
      height: options.height || 80,
      zIndex: objects.length,
      ...options
    };

    setObjects(prev => [...prev, newObject]);
    setSelectedIds([newObject.id]);
    sendObjectCreated(newObject);
    return newObject;
  };

  // Update object
  const updateObject = (id, updates) => {
    if (permission !== 'edit') return;
    setObjects(prev => prev.map(obj =>
      obj.id === id ? { ...obj, ...updates } : obj
    ));
    sendObjectUpdated(id, updates);
  };

  // Update with history
  const updateObjectWithHistory = (id, updates) => {
    updateObject(id, updates);
  };

  // Delete objects
  const deleteObjects = (ids) => {
    if (permission !== 'edit') {
      toast.error('You only have view access to this board');
      return;
    }
    setObjects(prev => prev.filter(obj => !ids.includes(obj.id)));
    setSelectedIds([]);
    sendObjectDeleted(ids);
  };

  // Dummy functions for view-only mode
  const undo = () => {};
  const redo = () => {};
  const canUndo = false;
  const canRedo = false;
  const alignObjects = () => {};

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-[#64748B]">Loading shared whiteboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold text-gray-800">Oops!</h1>
          <p className="text-[#64748B]">{error}</p>
          <Button onClick={() => navigate('/')}>Go to Homepage</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#F8FAFC] overflow-hidden">
      {/* Header */}
      <header className="h-14 bg-white border-b border-[#E2E8F0] flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-[#64748B]"
          >
            <ArrowLeft size={18} className="mr-1" />
            Home
          </Button>
          
          <div className="h-6 w-px bg-[#E2E8F0]" />
          
          <h1 className="text-lg font-semibold text-[#1E293B]">
            {boardName}
          </h1>
          
          {/* Permission badge */}
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
            permission === 'edit' 
              ? 'bg-green-100 text-green-700' 
              : 'bg-blue-100 text-blue-700'
          }`}>
            {permission === 'edit' ? <Edit3 size={12} /> : <Eye size={12} />}
            {permission === 'edit' ? 'Can Edit' : 'View Only'}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Real-time collaboration status */}
          {permission === 'edit' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50">
                    {isConnected ? (
                      <Wifi size={16} className="text-green-500" />
                    ) : (
                      <WifiOff size={16} className="text-gray-400" />
                    )}
                    {users.length > 0 && (
                      <div className="flex -space-x-2">
                        {users.slice(0, 3).map((u, i) => (
                          <div
                            key={u.id || i}
                            className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-medium"
                            style={{ backgroundColor: u.color || '#6366F1' }}
                          >
                            {u.name?.[0]?.toUpperCase() || '?'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isConnected ? `${users.length} user${users.length !== 1 ? 's' : ''} online` : 'Connecting...'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          
          {!isAuthenticated && (
            <Button size="sm" onClick={() => navigate('/login')}>
              Sign in to save
            </Button>
          )}
        </div>
      </header>

      {/* Main Canvas Area */}
      <div className="flex-1 relative">
        {permission === 'edit' && (
          <WhiteboardToolbar 
            activeTool={activeTool}
            setActiveTool={setActiveTool}
            connectorType={connectorType}
            setConnectorType={setConnectorType}
            zoom={zoom}
            setZoom={setZoom}
            gridType={gridType}
            setGridType={setGridType}
            snapToGrid={snapToGrid}
            setSnapToGrid={setSnapToGrid}
            undo={undo}
            redo={redo}
            canUndo={canUndo}
            canRedo={canRedo}
            penSettings={penSettings}
            setPenSettings={setPenSettings}
            shapeSettings={shapeSettings}
            setShapeSettings={setShapeSettings}
            selectedIds={selectedIds}
            onAlign={alignObjects}
          />
        )}
        
        <WhiteboardCanvas
          objects={objects}
          selectedIds={permission === 'edit' ? selectedIds : []}
          setSelectedIds={permission === 'edit' ? setSelectedIds : () => {}}
          activeTool={permission === 'edit' ? activeTool : 'select'}
          setActiveTool={permission === 'edit' ? setActiveTool : () => {}}
          connectorType={connectorType}
          zoom={zoom}
          setZoom={setZoom}
          pan={pan}
          setPan={setPan}
          gridType={gridType}
          snapToGrid={snapToGrid}
          addObject={permission === 'edit' ? addObject : () => {}}
          updateObject={permission === 'edit' ? updateObject : () => {}}
          updateObjectWithHistory={permission === 'edit' ? updateObjectWithHistory : () => {}}
          penSettings={penSettings}
          shapeSettings={shapeSettings}
          remoteCursors={remoteCursors}
          onCursorMove={permission === 'edit' ? sendCursorMove : undefined}
        />
        
        {/* View only overlay hint */}
        {permission === 'view' && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
            <Eye size={14} className="inline mr-2" />
            View only mode - Sign in to collaborate
          </div>
        )}
      </div>
    </div>
  );
};
